import {
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
  useEffect,
  ReactElement,
  cloneElement,
} from "react";

import { CSSObject } from "@emotion/react";
import { useOnClickOff } from "@newsela/angelou";

import {
  absoluteContainerCss,
  getContainerPositionCss,
  subNavMenuPositionCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/NavButtonWithPopout/NavButtonWithPopout.styles";
import { FOCUSABLE_SELECTOR } from "./NavButtonWithPopout.utils";

const NavButtonWithPopout = ({
  id,
  isOpen,
  setIsOpen,
  position,
  isSubNavMenu,
  rootCustomCss = {},
  managesFocusInternally = false,
  ButtonComponent,
  PopoutComponent,
}: INavMenuPopout) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoutRef = useRef<HTMLDivElement | null>(null);
  const popoutFocusableRef = useRef<HTMLElement[]>([]);
  const focusedRef = useRef<HTMLElement | null>(null);

  const closeMenu = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const isEventFromSelf = target.id === id || target.classList.contains(id);

    if (!isEventFromSelf) {
      setIsOpen(false);
    }
  };

  const clickOffRef = useOnClickOff(closeMenu);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      focusedRef.current = e.target as HTMLElement;
    };

    window.addEventListener("focusin", onFocusIn, true);
    return () => window.removeEventListener("focusin", onFocusIn, true);
  }, []);

  const isVisible = (el: HTMLElement | null) => {
    if (!el) return false;

    try {
      const style = window.getComputedStyle(el);
      if (style.visibility === "hidden" || style.display === "none") {
        return false;
      }
      return el.getClientRects().length > 0;
    } catch {
      return true;
    }
  };

  const collectFocusable = (container: HTMLElement) => {
    const found = Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );

    return found.filter(isVisible);
  };

  const getPopoutFocusables = () => {
    if (popoutFocusableRef.current.length) {
      return popoutFocusableRef.current;
    }

    if (!popoutRef.current) return [];

    const focusables = collectFocusable(popoutRef.current);
    popoutFocusableRef.current = focusables;
    return focusables;
  };

  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      popoutRef.current = node;

      if (typeof clickOffRef === "function") {
        clickOffRef(node as any);
      } else if (clickOffRef && "current" in clickOffRef) {
        (clickOffRef as any).current = node;
      }

      if (!node) {
        popoutFocusableRef.current = [];
        return;
      }

      if (!managesFocusInternally) {
        const focusables = collectFocusable(node);
        popoutFocusableRef.current = focusables;
      }
    },
    [clickOffRef],
  );

  const closeMenuOnEscape = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") return;

    event.stopPropagation();
    triggerRef.current?.focus();
    setIsOpen(false);
  };

  const onPopoutKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Tab") {
      return;
    }

    const focusable = popoutFocusableRef.current;
    if (!focusable.length) return;

    const activeEl = focusedRef.current;
    const currentIndex = activeEl ? focusable.indexOf(activeEl) : -1;

    const isNext = e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey);

    const isPrev = e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey);

    if (!isNext && !isPrev) return;

    e.preventDefault();

    if (isNext) {
      const nextIndex =
        currentIndex === -1 || currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;

      focusable[nextIndex]?.focus();
    }

    if (isPrev) {
      const prevIndex =
        currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;

      focusable[prevIndex]?.focus();
    }
  }, []);

  const onRootKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        closeMenuOnEscape(event);
        return;
      }

      if (!isOpen) return;
      if (focusedRef.current !== triggerRef.current) return;

      const focusables = getPopoutFocusables();
      if (!focusables.length) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        focusables[0]?.focus();
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        focusables[focusables.length - 1]?.focus();
      }
    },
    [isOpen],
  );

  const buttonWithRef = cloneElement(ButtonComponent, {
    ref: triggerRef,
    id,
    "aria-haspopup": "menu",
    "aria-expanded": isOpen,
    type: "button",
  });

  const { top, right, bottom, left } = position;

  return (
    <li
      onKeyDown={onRootKeyDown}
      css={[getContainerPositionCss(isSubNavMenu), rootCustomCss]}
    >
      {buttonWithRef}

      {isOpen && (
        <div
          ref={combinedRef}
          id={`${id}-menu`}
          role="menu"
          tabIndex={managesFocusInternally ? -1 : 0}
          aria-labelledby={id}
          onKeyDown={onPopoutKeyDown}
          data-testid="navmenupopout-container"
          css={[
            absoluteContainerCss,
            { top, right, bottom, left },
            isSubNavMenu && subNavMenuPositionCss,
          ]}
        >
          {PopoutComponent}
        </div>
      )}
    </li>
  );
};

export interface IAbsolutePosition {
  top?: number | string | undefined;
  right?: number | string | undefined;
  bottom?: number | string | undefined;
  left?: number | string | undefined;
}

interface INavMenuPopout {
  id: string;
  isOpen: boolean;
  isSubNavMenu?: boolean;
  hasSubNavMenu?: boolean;
  rootCustomCss?: CSSObject;
  position: IAbsolutePosition;
  managesFocusInternally?: boolean;
  ButtonComponent: ReactElement<
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
      ref?: React.Ref<HTMLButtonElement>;
    }
  >;
  PopoutComponent: ReactElement;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

NavButtonWithPopout.displayName = "NavButtonWithPopout";
export { NavButtonWithPopout };
